<script setup>
import { computed, ref, watch } from 'vue';
import Checkbox from 'dashboard/components-next/checkbox/Checkbox.vue';
import { RECHECK_DELAY_UNITS } from './constants';

const props = defineProps({
  modelValue: {
    type: Number,
    default: null,
  },
});

const emit = defineEmits(['update:modelValue']);

const enabled = ref(false);
const amount = ref(null);
const unit = ref(RECHECK_DELAY_UNITS[0].seconds);

// The delay is stored as plain seconds, so the unit shown is the largest one
// the stored value divides into evenly: 1800 reads as 30 minutes, not 1800
// seconds.
const applyStoredValue = seconds => {
  enabled.value = Boolean(seconds);
  if (!seconds) return;

  const largestFittingUnit = [...RECHECK_DELAY_UNITS]
    .reverse()
    .find(({ seconds: size }) => seconds % size === 0);

  unit.value = largestFittingUnit.seconds;
  amount.value = seconds / largestFittingUnit.seconds;
};

watch(() => props.modelValue, applyStoredValue, { immediate: true });

const emitValue = () => {
  const seconds = enabled.value ? Number(amount.value) * unit.value : null;
  emit('update:modelValue', seconds > 0 ? seconds : null);
};

const onToggle = () => {
  if (!enabled.value) amount.value = null;
  emitValue();
};

const unitOptions = computed(() =>
  RECHECK_DELAY_UNITS.map(({ key, seconds }) => ({ key, seconds }))
);
</script>

<template>
  <div class="mt-3">
    <label class="flex items-center gap-2 mb-0 cursor-pointer w-fit">
      <Checkbox v-model="enabled" @change="onToggle" />
      <span class="text-sm text-n-slate-11">
        {{ $t('AUTOMATION.RECHECK_DELAY.LABEL') }}
      </span>
    </label>

    <div v-if="enabled" class="flex items-center gap-2 mt-2">
      <input
        v-model="amount"
        type="number"
        min="1"
        class="w-24 mb-0"
        :placeholder="$t('AUTOMATION.RECHECK_DELAY.PLACEHOLDER')"
        @input="emitValue"
      />
      <select v-model="unit" class="w-32 mb-0" @change="emitValue">
        <option
          v-for="option in unitOptions"
          :key="option.key"
          :value="option.seconds"
        >
          {{ $t(`AUTOMATION.RECHECK_DELAY.UNITS.${option.key}`) }}
        </option>
      </select>
    </div>

    <p class="mt-1 mb-0 text-xs text-n-slate-10">
      {{ $t('AUTOMATION.RECHECK_DELAY.HELP') }}
    </p>
  </div>
</template>
